import { Link } from 'react-router-dom';
import TabOutlinedIcon from '@mui/icons-material/TabOutlined';
import FlashOnOutlinedIcon from '@mui/icons-material/FlashOnOutlined';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import DevicesOtherOutlinedIcon from '@mui/icons-material/DevicesOtherOutlined';
import type { SvgIconComponent } from '@mui/icons-material';
import { PageHero } from '@/components/PageHero';
import { NotifyForm } from '@/components/NotifyForm';
import { Reveal } from '@/components/Reveal';
import { useSeo } from '@/seo/useSeo';

type Perk = { icon: SvgIconComponent; title: string; body: string; linkTo?: string };

const perks: Perk[] = [
  {
    icon: TabOutlinedIcon,
    title: 'Fewer tabs, not more',
    body: 'The device\'s monitoring history, its ticket history, and its documentation are the same record — not three tools you have to check separately before you can even start diagnosing.',
    linkTo: '/platform/helpdesk-ticketing',
  },
  {
    icon: FlashOnOutlinedIcon,
    title: 'AI drafts the boring first pass',
    body: 'A new ticket already has an AI-drafted diagnosis and a suggested reply waiting when you open it — you\'re reviewing and refining, not staring at a blank reply box while the requester waits.',
    linkTo: '/blog/what-agentic-ai-means-for-msps',
  },
  {
    icon: MenuBookOutlinedIcon,
    title: 'Documentation attached to the thing it describes',
    body: 'The network diagram, the runbook, the client-specific procedure — it\'s linked to the device or client you\'re already looking at, not filed in a wiki you have to remember exists.',
    linkTo: '/blog/it-documentation-that-lives-where-the-work-happens',
  },
  {
    icon: DevicesOtherOutlinedIcon,
    title: 'One console, whatever the client runs',
    body: 'Jamf-managed Macs, Intune-managed Windows machines, and Nexus-native endpoints show up in the same device view — you\'re not learning a fourth vendor portal because one client standardized differently.',
    linkTo: '/integrations',
  },
];

export function SolutionsTechnicians(): JSX.Element {
  useSeo({
    title: 'For the techs doing the work',
    description:
      'Fewer tabs, AI-drafted first-pass diagnoses, documentation attached to the record it describes, and one console regardless of what MDM a client runs — built for the person actually holding the ticket.',
    path: '/solutions/technicians',
  });

  return (
    <div>
      <PageHero
        eyebrow="For the techs who do the work"
        title="Built for the person actually holding the ticket."
        subtitle="A platform pitch usually talks to the owner signing the check. This page is for whoever's going to open forty tickets a day in it."
      >
        <Reveal delay={150}>
          <div className="mt-9 flex justify-center">
            <NotifyForm />
          </div>
        </Reveal>
      </PageHero>

      <section className="py-16 md:py-24">
        <div className="container-content">
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            {perks.map((p, i) => {
              const Icon = p.icon;
              const card = (
                <div className="group h-full rounded-2xl border border-white/10 bg-white/[0.02] p-6 transition-all duration-200 hover:-translate-y-1 hover:border-nexus-purple/50">
                  <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl bg-nexus-purple/[0.14] text-nexus-purple-light transition-transform duration-200 group-hover:scale-110">
                    <Icon fontSize="small" />
                  </div>
                  <h3 className="font-display text-[17px] font-semibold text-nexus-ink">{p.title}</h3>
                  <p className="mt-1.5 text-sm text-white/65">{p.body}</p>
                </div>
              );
              return (
                <Reveal key={p.title} delay={i * 90}>
                  {p.linkTo ? (
                    <Link to={p.linkTo} className="block no-underline">
                      {card}
                    </Link>
                  ) : (
                    card
                  )}
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <section className="border-t border-white/10 py-16 md:py-24">
        <div className="container-content max-w-[760px]">
          <Reveal>
            <h2 className="font-display text-2xl font-bold text-nexus-ink md:text-3xl">
              What Nexus doesn&apos;t take away from you
            </h2>
            <p className="mt-4 text-[15px] leading-relaxed text-white/75">
              An AI-drafted diagnosis is a draft. Nothing destructive or irreversible executes
              without a human approving it first — the agent does the first-pass legwork, the
              judgment stays yours. See the{' '}
              <Link to="/glossary/agentic-ai" className="text-nexus-purple-light no-underline hover:underline">
                agentic AI
              </Link>{' '}
              glossary entry for the exact boundary.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="border-t border-white/10 py-16 md:py-24">
        <div className="container-content">
          <Reveal>
            <div className="rounded-[28px] border border-nexus-purple/35 bg-gradient-to-br from-[#2a1450] to-[#120a26] p-8 text-center md:p-16">
              <h2 className="font-display text-3xl font-bold text-nexus-ink md:text-[36px]">
                Ask your owner about the design-partner cohort.
              </h2>
              <p className="mx-auto mt-3 max-w-[520px] text-white/70">
                Or sign up yourself and forward it along — we&apos;ll take the conversation from
                there.
              </p>
              <div className="mt-8 flex justify-center">
                <NotifyForm />
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
